<template>
    <div class="work-location-card">
        <div class="work-location-header">
            <div>
                <h3>Work Location</h3>
                <p class="subtitle">Choose Your Work Location for Today</p>
            </div>
            <div class="animated-button-container">
                <AnimatedButton />
            </div>
        </div>

        <div class="work-location-body">
            <div class="location-tabs">
                <div class="tab-item" :class="{ active: workLocation === 'inOffice' }"
                    @click="workLocation = 'inOffice'">
                    <i class="mdi mdi-briefcase"></i> In-Office >
                </div>
                <div class="tab-item" :class="{ active: workLocation === 'remoteWork' }"
                    @click="workLocation = 'remoteWork'">
                    <i class="mdi mdi-home"></i> Remote Work >
                </div>
            </div>

            <div class="tab-content">
                <!-- In-Office Section -->
                <div v-if="workLocation === 'inOffice'" class="in-office-grid">
                    <div v-for="(card, index) in inOfficeCards" :key="index" class="info-card" :class="card.background">
                        <div class="text-content">
                            <p class="title">{{ card.title }}</p>
                            <p class="desc">{{ card.desc }}</p>
                        </div>
                        <img :src="card.image" class="info-image" />
                    </div>
                </div>

                <!-- Remote Work Section -->
                <div v-else class="remote-work-card">
                    <!-- Not Configured State -->
                    <div v-if="!isConfigured" class="error-state">
                        <v-icon size="64" color="warning">mdi-alert-circle-outline</v-icon>
                        <p class="error-title">Remote Work Not Configured</p>
                        <p class="error-desc">Please contact your administrator to set up Remote Work for this campus</p>
                    </div>

                    <!-- Configured State -->
                    <div v-else>
                        <div class="section-header">Select Your Working Hours</div>

                        <div class="today-date-row">
                            <div class="today-label">Today</div>
                            <div class="today-date">{{ moment().format('DD/MM/YYYY') }}</div>
                        </div>

                        <v-row dense class="selects-grid">
                            <v-col cols="12" md="6" sm="6">
                                <v-select v-model="startTime" :items="times" label="Work Start"
                                    :disabled="isSaveDisabled || isLoading" variant="outlined" density="comfortable"
                                    rounded class="time-select" />
                            </v-col>

                            <v-col cols="12" md="6" sm="6">
                                <v-select v-model="endTime" :items="times" label="Work End"
                                    :disabled="isSaveDisabled || isLoading" variant="outlined" density="comfortable"
                                    rounded class="time-select" />
                            </v-col>

                            <v-col cols="12" md="6" sm="6">
                                <v-select v-model="lunchDuration" :items="lunchDurations" :disabled="isSaveDisabled || isLoading"
                                    label="Lunch Break" variant="outlined" density="comfortable"
                                    rounded class="time-select" />
                            </v-col>

                            <v-col cols="12" md="6" sm="6">
                                <v-combobox
                                    v-model="note"
                                    :items="noteSuggestions"
                                    label="Optional Note (e.g., status, focus time)"
                                    :disabled="isSaveDisabled || isLoading"
                                    variant="outlined"
                                    density="comfortable"
                                    rounded
                                    maxlength="200"
                                    counter
                                    clearable
                                    class="time-select"
                                    hint="Choose a suggestion or type your own"
                                    persistent-hint
                                />
                            </v-col>
                        </v-row>

                        <v-alert v-if="errorMessage" type="error" class="mt-3" dense closable
                            @click:close="errorMessage = ''">
                            {{ errorMessage }}
                        </v-alert>

                        <v-alert v-if="successMessage" type="success" class="mt-3" dense closable
                            @click:close="successMessage = ''">
                            {{ successMessage }}
                        </v-alert>

                        <div class="save-button-container">
                            <v-btn v-if="!isSaveDisabled" color="primary" rounded class="save-button" :loading="isLoading"
                                @click="saveSchedule">
                                Save
                            </v-btn>

                            <v-btn v-else color="secondary" rounded variant="outlined" class="save-button"
                                @click="editSchedule">
                                Edit
                            </v-btn>

                            <p v-if="isSaveDisabled" class="save-hint">
                                Schedule saved for today
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import moment from 'moment'
import AnimatedButton from '@/components/general/ChildComponents/AnimatedButton.vue'
import snacks from '@/assets/Branding/Pia/snacks.png'
import lunch from '@/assets/Branding/Pia/lunch.png'
import meeting from '@/assets/Branding/Pia/meeting.png'
import fun from '@/assets/Branding/Pia/fun.png'
import {
    getTodayRemoteWorkRecord,
    saveRemoteWorkRecord,
    validateTimes,
    isRemoteWorkConfigured
} from '@/services/RemoteWorkService'

const workLocation = ref('inOffice')
const startTime = ref('09:00')
const endTime = ref('18:00')
const lunchDuration = ref('30')
const note = ref('')
const isSaveDisabled = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const isConfigured = ref(true)
const currentDate = ref(moment().format('DD/MM/YYYY'))

const times = [
    '06:00', '06:30', '07:00', '07:30', '08:00', '08:30',
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00'
]

const lunchDurations = [
    { title: 'No Break', value: '0' },
    { title: '30 min', value: '30' },
    { title: '45 min', value: '45' },
    { title: '1 hour', value: '60' }
]

const noteSuggestions = [
    'Available all day',
    'Focused work - limited availability',
    'Meetings scheduled',
    'Available for calls',
    'Deep work session',
    'Flexible schedule',
    'Team collaboration time',
    'Client calls scheduled',
    'Training session',
    'Limited availability - urgent only'
]

const inOfficeCards = [
    {
        title: 'Enjoy Snacks',
        desc: 'Free snacks available',
        image: snacks,
        background: 'card-blue'
    },
    {
        title: 'Enjoy Lunch',
        desc: 'Free lunch provided',
        image: lunch,
        background: 'card-yellow'
    },
    {
        title: 'Enjoy Break',
        desc: 'Take regular breaks',
        image: meeting,
        background: 'card-lightblue'
    },
    {
        title: 'Enjoy Events',
        desc: 'Team events and activities',
        image: fun,
        background: 'card-purple'
    }
]

// Check configuration and load data
onMounted(async () => {
    try {
        isConfigured.value = await isRemoteWorkConfigured()
        if (isConfigured.value) {
            await loadTodayRecord()
        }
    } catch (error) {
        console.error('Error loading remote work data:', error)
        isConfigured.value = false
    }
})

// Load today's record
const loadTodayRecord = async () => {
    try {
        isLoading.value = true
        const record = await getTodayRemoteWorkRecord()

        if (record) {
            startTime.value = record.startTime
            endTime.value = record.endTime
            lunchDuration.value = record.breakDuration || '0'
            note.value = record.note || ''
            isSaveDisabled.value = true
        }
    } catch (error) {
        console.error('Error loading today record:', error)
    } finally {
        isLoading.value = false
    }
}

// Watch for date changes (reset at midnight)
watch(() => moment().format('DD/MM/YYYY'), async (newDate) => {
    if (newDate !== currentDate.value) {
        currentDate.value = newDate
        isSaveDisabled.value = false
        errorMessage.value = ''
        successMessage.value = ''
        if (isConfigured.value) {
            await loadTodayRecord()
        }
    }
})

// Save schedule
const saveSchedule = async () => {
    try {
        errorMessage.value = ''
        successMessage.value = ''

        // Validate times
        const validation = validateTimes(startTime.value, endTime.value, lunchDuration.value)
        if (!validation.valid) {
            errorMessage.value = validation.error
            return
        }

        isLoading.value = true

        await saveRemoteWorkRecord(
            startTime.value,
            endTime.value,
            lunchDuration.value,
            note.value
        )

        isSaveDisabled.value = true
        successMessage.value = 'Schedule saved successfully!'
    } catch (error) {
        errorMessage.value = 'Error saving schedule. Please try again.'
        console.error('Error saving schedule:', error)
    } finally {
        isLoading.value = false
    }
}

const editSchedule = () => {
    isSaveDisabled.value = false
    successMessage.value = ''
}
</script>

<style scoped>
/* --- Main Card --- */
.work-location-card {
    background: white;
    border-radius: 14px;
    padding: 30px;
    margin: 0 auto;
    margin-top: 20px;
    position: relative;
    z-index: 10;
    max-width: calc(100vw - 20%);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* --- Header --- */
.work-location-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.subtitle {
    color: #718096;
    margin-top: 4px;
    font-size: 17px;
}

.animated-button-container {
    display: flex;
    justify-content: flex-end;
}

/* --- Body --- */
.work-location-body {
    display: flex;
    margin-top: 1rem;
}

.location-tabs {
    width: 180px;
    border-right: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
}

.tab-item {
    padding: 12px;
    cursor: pointer;
    font-weight: 500;
    color: #374151;
    transition: background 0.2s ease;
}

.tab-item:hover {
    background-color: #f3f4f6;
}

.tab-item.active {
    color: #2563eb;
    font-weight: bold;
    background-color: #e0f2fe;
    border-right: 4px solid #2563eb;
}

.tab-content {
    flex: 1;
    padding-left: 20px;
}

/* --- In-Office Grid --- */
.in-office-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
}

.info-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    min-height: 130px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.info-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
    cursor: pointer;
}

.text-content {
    flex: 1;
    padding-right: 8px;
}

.title {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 4px;
    color: #111827;
}

.desc {
    font-size: 13px;
    color: #6b7280;
}

.info-image {
    width: 110px;
    height: 90px;
    object-fit: cover;
    clip-path: polygon(40% 0%, 100% 0%, 100% 100%, 0 100%);
}

/* --- Remote Work Section --- */
.remote-work-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.error-state {
    text-align: center;
    padding: 60px 20px;
}

.error-title {
    font-weight: bold;
    font-size: 20px;
    margin-top: 20px;
    color: #374151;
}

.error-desc {
    font-size: 15px;
    color: #6b7280;
    margin-top: 12px;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
}

.section-header {
    font-weight: bold;
    font-size: 16px;
}

.today-date-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #e5e7eb;
    padding: 10px 20px;
    border-radius: 10px;
    font-weight: bold;
    color: #374151;
}

.today-label {
    font-size: 16px;
}

.today-date {
    font-size: 16px;
    color: #3b82f6;
}

.selects-grid {
    margin-top: 10px;
}

.time-select {
    width: 100%;
}

.save-button-container {
    align-self: flex-end;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-top: 10px;
}

.save-button {
    min-width: 120px;
}

.save-hint {
    font-size: 12px;
    color: #10b981;
    margin-top: 5px;
    text-align: right;
    font-weight: 500;
}

/* Fix select field height */
.v-input {
    min-height: 50px;
}

/* --- Background Colors --- */
.card-blue {
    background: #eff6ff;
}

.card-yellow {
    background: #fef9c3;
}

.card-lightblue {
    background: #e0f2fe;
}

.card-purple {
    background: #f3e8ff;
}

/* --- Media Queries --- */
@media (max-width: 1200px) {
    .info-image {
        width: 100px;
        height: 80px;
    }
}

@media (max-width: 992px) {
    .work-location-body {
        flex-direction: column;
    }

    .location-tabs {
        flex-direction: row;
        width: 100%;
        border-right: none;
        border-bottom: 1px solid #e0e0e0;
        margin-bottom: 10px;
    }

    .tab-item {
        flex: 1;
        text-align: center;
    }

    .tab-content {
        padding-left: 0;
    }

    .in-office-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 850px) {
    .work-location-card {
        max-width: calc(100vw - 10%);
    }

    .info-image {
        width: 90px;
        height: 70px;
    }

    .info-card {
        min-height: 110px;
    }

    .title {
        font-size: 14px;
    }

    .desc {
        font-size: 12px;
    }
}
</style>